import type { FC } from "react";

import { Dialog } from "@ui/components/Dialog";
import { Form, useForm } from "@ui/components/Form";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import dompurify from "isomorphic-dompurify";

import { Accordion, AccordionItem } from "@jti/core-components/components/Accordion";
import { Button } from "@jti/core-components/components/Button";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import { toast } from "@jti/core-components/components/Toaster";
import type { DeviceAllowedCodeLength } from "@jti/fe-common";
import { DEFAULT_ALLOWED_DEVICE_CODE_LENGTH, getErrorMessageFromError, removeSpacesFromCode } from "@jti/fe-common";
import { useDevices } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { RegisterDeviceInput } from "./components";

import styles from "./RegisterDeviceDialog.module.scss";

const { sanitize } = dompurify;

export type RegisterDeviceAccordionImagesContent = {
  imageUrl: string;
  imageAlt: string;
  description: string;
};

export type RegisterDeviceDialogProps = {
  accordionContentVariant?: "default" | "withImages";
  title: string;
  theme?: "dark" | "light";
  imgMobile: string;
  isOpened?: boolean;
  imgDesktop: string;
  cancelButtonText: string;
  description: string;
  confirmButtonText: string;
  imageAlt: string;
  registerDeviceAccordionTitle: string;
  registerDeviceAccordionButtonUrl: string;
  registerDeviceAccordionButtonText: string;
  registerDeviceAccordionDescription?: string;
  registerDeviceAccordionImagesContent?: RegisterDeviceAccordionImagesContent[];
  requiredCodeLength: DeviceAllowedCodeLength[];
  onClose?: () => void;
  onDeviceRegistrationSuccess?: () => void;
  isDeviceCodeFormatLabelVisible?: boolean;
};
const REGISTER_DEVICE_FIELD_NAME = "registrationCode" as const;

type FromValues = {
  [REGISTER_DEVICE_FIELD_NAME]: string;
};

export const RegisterDeviceDialog: FC<RegisterDeviceDialogProps> = ({
  accordionContentVariant = "default",
  theme = "light",
  isOpened = false,
  imageAlt,
  imgMobile,
  imgDesktop,
  description,
  cancelButtonText,
  confirmButtonText,
  registerDeviceAccordionTitle,
  registerDeviceAccordionButtonUrl,
  registerDeviceAccordionButtonText,
  registerDeviceAccordionDescription = "",
  registerDeviceAccordionImagesContent = [],
  requiredCodeLength = DEFAULT_ALLOWED_DEVICE_CODE_LENGTH,
  onClose,
  onDeviceRegistrationSuccess,
  title,
  isDeviceCodeFormatLabelVisible = true,
}) => {
  const { __, dictionary } = useTranslations();
  const form = useForm<FromValues>({ delayError: 1000, mode: "onSubmit" });
  const { isLoadingRegisterDevice, registerDevice, isRegisterDeviceError, resetRegisterDevice, registerDeviceError } =
    useDevices();
  const { breakpoint } = useDeviceBreakpoint();
  const isSmallScreen = breakpoint === "mobile";
  const isMediumScreen = breakpoint === "tablet";

  const {
    handleSubmit,
    formState: { isValid, errors },
    clearErrors,
  } = form;

  const buttonSize = isSmallScreen || isMediumScreen ? "sm" : "md";

  const isAccordionContentDefault = accordionContentVariant === "default" && !!registerDeviceAccordionDescription;
  const isAccordionContentWithImages =
    accordionContentVariant === "withImages" && !!registerDeviceAccordionImagesContent.length;

  const formErrorMessage = errors?.[REGISTER_DEVICE_FIELD_NAME]?.message;

  const getDeviceRegistrationError = () => {
    if (formErrorMessage) {
      return formErrorMessage;
    }
    if (isRegisterDeviceError) {
      return getErrorMessageFromError(registerDeviceError, __("evo.fallback.error"), dictionary);
    }
    return __("auth.registration.device.notification.device_not_suitable");
  };

  const handleClearError = () => {
    if (formErrorMessage) {
      clearErrors(REGISTER_DEVICE_FIELD_NAME);
    }
    if (isRegisterDeviceError) {
      resetRegisterDevice();
    }
  };

  const handleRegisterDeviceSubmit = handleSubmit(async ({ registrationCode }) => {
    try {
      const registerDeviceResponse = await registerDevice(removeSpacesFromCode(registrationCode));
      toast.success(__("profile.devices.register_new.success"));
      dispatchTrackingEvent(TrackingEvents.TrackingUserAccountDeviceRegister, {
        device_reference: registerDeviceResponse?.data?.deviceId,
        cta_name: __("profile.devices.register_new"),
      });

      dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
        cta_name: confirmButtonText,
        cta_location: "register_device_dialog",
      });

      onDeviceRegistrationSuccess?.();
    } catch (error) {
      const preparedError = getErrorMessageFromError(error ?? {}, __("evo.fallback.error"), dictionary);
      toast.error(preparedError);
    }
  });

  const onDialogClose = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: cancelButtonText,
      cta_location: "register_device_dialog",
    });

    onClose?.();
  };

  const onRegisterDeviceAccordionButtonClick = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: registerDeviceAccordionButtonText,
      cta_location: "register_device_dialog",
    });

    window.location.href = registerDeviceAccordionButtonUrl;
  };

  return (
    <Dialog
      theme={theme}
      noPadding={true}
      closable={false}
      isOpened={isOpened}
      fullscreen={isSmallScreen}
      className={styles.dialog}
      withContentPadding={false}
    >
      <div className={styles.container}>
        <div className={styles.contentWrapper}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.description}>{description}</p>
          <Accordion>
            <AccordionItem id="registerDeviceAccordion" title={registerDeviceAccordionTitle}>
              <div className={styles.accordionContentWrapper}>
                {isAccordionContentDefault && (
                  <div
                    className={styles.accordionContentText}
                    dangerouslySetInnerHTML={{
                      __html: sanitize(registerDeviceAccordionDescription),
                    }}
                  />
                )}
                {isAccordionContentWithImages && (
                  <>
                    {registerDeviceAccordionImagesContent.map(({ description, imageAlt, imageUrl }) => (
                      <div className={styles.accordionImageSectionWrapper} key={imageUrl}>
                        <img className={styles.accordionImage} src={imageUrl} alt={imageAlt} />
                        <p className={styles.accordionImageSectionText}>{description}</p>
                      </div>
                    ))}
                  </>
                )}
                <Button variant="text" size="sm" onClick={onRegisterDeviceAccordionButtonClick}>
                  {registerDeviceAccordionButtonText}
                </Button>
              </div>
            </AccordionItem>
          </Accordion>
          <Form className={styles.form} form={form}>
            <RegisterDeviceInput
              onCleanErrors={handleClearError}
              fieldName={REGISTER_DEVICE_FIELD_NAME}
              requiredCodeLength={requiredCodeLength}
              isDeviceRegistrationError={isRegisterDeviceError || Boolean(formErrorMessage)}
              deviceRegistrationErrorText={getDeviceRegistrationError()}
              isInputHelperInfoTextVisible={isDeviceCodeFormatLabelVisible}
            />
          </Form>
          <div className={styles.buttonsWrapper}>
            <Button
              size={buttonSize}
              disabled={!isValid || isRegisterDeviceError}
              className={styles.button}
              pending={isLoadingRegisterDevice}
              onClick={handleRegisterDeviceSubmit}
              data-testid="register-device-button"
            >
              {confirmButtonText}
            </Button>

            <Button
              variant="secondary"
              onClick={onDialogClose}
              size={buttonSize}
              className={styles.button}
              data-testid="secondary-button"
            >
              {cancelButtonText}
            </Button>
          </div>
        </div>
        <div className={styles.imageWrapper}>
          <PictureBehaviour
            className={styles.image}
            sources={[
              { srcSet: imgDesktop, media: "(min-width: 768px)" },
              { srcSet: imgMobile, media: "(max-width: 767px)" },
            ]}
            imageProps={{
              src: imgDesktop,
              alt: imageAlt,
              className: styles.image,
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};
